/**
 * useTheme.js
 * ─────────────────────────────────────────────────────────────────
 * Controls Trekko's light / dark mode.
 *
 * Strategy:
 *  • Tailwind's `darkMode: "class"` toggles the `dark` class on <html>.
 *  • Preference is persisted to localStorage so it survives reloads.
 *  • On first visit, the user's OS preference (prefers-color-scheme) is
 *    respected as the default.
 *
 * Usage inside any Vue component / composable:
 *
 *   import { useTheme } from "@/composables/useTheme";
 *
 *   const { isDark, toggle, setLight, setDark } = useTheme();
 *
 * In a template:
 *   <button @click="toggle">{{ isDark ? '☀️ Light' : '🌙 Dark' }}</button>
 * ─────────────────────────────────────────────────────────────────
 */

import { ref, watch, onMounted } from "vue";

// ── Storage key ──────────────────────────────────────────────────
const STORAGE_KEY = "trekko-theme";

// ── Shared reactive state (module-level singleton) ───────────────
// All components that call useTheme() share the same `isDark` ref,
// so toggling in one place updates every consumer instantly.
const isDark = ref(false);

// ── Helpers ──────────────────────────────────────────────────────
function applyTheme(dark) {
	const html = document.documentElement;
	if (dark) {
		html.classList.add("dark");
	} else {
		html.classList.remove("dark");
	}
}

function persist(dark) {
	try {
		localStorage.setItem(STORAGE_KEY, dark ? "dark" : "light");
	} catch {
		// localStorage may be unavailable in some environments
	}
}

function readStorage() {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

function prefersDark() {
	return (
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-color-scheme: dark)").matches
	);
}

// ── Initialize once at module load ───────────────────────────────
// (Runs when the composable is first imported, not per-component.)
(function init() {
	const stored = readStorage();
	if (stored === "dark") {
		isDark.value = true;
	} else if (stored === "light") {
		isDark.value = false;
	} else {
		// No stored preference — fall back to OS setting
		isDark.value = prefersDark();
	}
	// Apply immediately to prevent a flash of the wrong theme
	applyTheme(isDark.value);
})();

// ── Keep DOM in sync whenever isDark changes ──────────────────────
watch(isDark, (val) => {
	applyTheme(val);
	persist(val);
});

// ── Composable ───────────────────────────────────────────────────
export function useTheme() {
	// Listen for OS-level theme changes and sync if no manual preference is set
	onMounted(() => {
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const handleOsChange = (e) => {
			// Only follow OS changes if the user hasn't manually picked a mode
			if (!readStorage()) {
				isDark.value = e.matches;
			}
		};
		mq.addEventListener("change", handleOsChange);
	});

	// ── Public API ───────────────────────────────────────────────
	return {
		/** Reactive boolean — true when dark mode is active */
		isDark,

		/** Flip between light and dark */
		toggle() {
			isDark.value = !isDark.value;
		},

		/** Force light mode */
		setLight() {
			isDark.value = false;
		},

		/** Force dark mode */
		setDark() {
			isDark.value = true;
		},

		/**
		 * Set by name — handy for a select/dropdown with multiple themes.
		 * @param {"light"|"dark"} mode
		 */
		setMode(mode) {
			isDark.value = mode === "dark";
		},
	};
}
